import React from "react";

import { css } from "@emotion/core";

import {
  Alert,
  Colors as ThemeColors,
  Typography,
} from "@zapier/design-system";

import { Border } from "@zapier/checkout/esm/constants";
import {
  useApolloInjection,
  withApolloInjectionProvider,
} from "@zapier/checkout/esm/contexts/ApolloInjection";
import { invoicePreviewQuery_account_invoicePreview as Invoice } from "@zapier/checkout/esm/graphql-types";
import { PlanV2 } from "@zapier/checkout/esm/types";
import { ApolloInjection, InvoiceType } from "@zapier/checkout/esm/types";
import { Currency, useCurrencies } from "@zapier/currency";

import { AmountDue } from "./AmountDue";
import { AmountDueOnNextInterval } from "./AmountDueOnNextInterval";
import { SummaryLine } from "./SummaryLine";
import { SummarySkeleton } from "./SummarySkeleton";
import { getIncludedMeteredTasks } from "./utils/getIncludedMeteredTasks";
import { setInvoicedAt } from "./utils/setInvoicedAt";

type Props = {
  invoice: Omit<Invoice, "__typename"> | null | undefined;
  error: string | null | undefined;
  isLoading: boolean;
  plan: Omit<PlanV2, "__typename">;
  invoiceType: InvoiceType;
} & ApolloInjection;

const sectionCss = {
  backgroundColor: "transparent",
  border: `${Border.size.default} solid ${ThemeColors.neutral400}`,
  borderRadius: Border.radius.default,
  overflow: "hidden",
  ...Typography.paragraph3,
};

const rebrandInvoiceTotalCss = css`
  font-weight: 700;
`;

/* Exported to be re-usable across all line items of the summary */
export const tableItem = css`
  color: ${ThemeColors.neutral800};
  display: grid;
  grid-template-columns: 70% 30%;
  border-bottom: 1px solid ${ThemeColors.neutral400};

  & > span {
    padding: 10px;

    &:nth-of-type(2) {
      text-align: right;
      border-left: 1px solid ${ThemeColors.neutral400};
    }
  }
  @media (max-width: 769px) {
    grid-template-columns: 50% 50%;
  }
`;

export const CheckoutSummaryComponent = ({
  error,
  invoice,
  isLoading,
  plan,
  invoiceType,
}: Props) => {
  const { useQuery } = useApolloInjection();
  const { currencies } = useCurrencies({ useQuery });
  const planNumFreeMeteredTasks = getIncludedMeteredTasks(plan);
  const tiers = plan.tiers;

  const currency = currencies?.find(
    (c: Currency) => c.name === invoice?.currency
  );

  if (invoiceType !== "overdue") {
    setInvoicedAt(invoice, isLoading);
  }

  if (isLoading || !invoice) {
    return <SummarySkeleton />;
  }

  if (error || !plan) {
    return <Alert type="error">{error}</Alert>;
  }

  return (
    <section css={sectionCss}>
      {invoice.items.length > 0 && (
        <>
          <div css={[rebrandInvoiceTotalCss, tableItem]}>
            <span>Description</span>
            <span>Amount</span>
          </div>
          {invoice.items.map((line: any) => (
            <SummaryLine key={line.id} line={line} />
          ))}
        </>
      )}
      <AmountDue amountDue={invoice.amountDue} currency={currency} />
      {plan.amount > 0 && (
        <AmountDueOnNextInterval
          currency={currency}
          extraTerm={invoice?.metadata?.terms}
          isPlanMetered={tiers.length > 0}
          meteredTiers={tiers}
          nextPaymentDate={invoice.periodEnd}
          planBillingAmount={plan.amount}
          planInterval={plan.interval}
          planNumFreeMeteredTasks={planNumFreeMeteredTasks}
          invoiceType={invoiceType}
        />
      )}
    </section>
  );
};

export const CheckoutSummary = withApolloInjectionProvider<Props>(
  CheckoutSummaryComponent
);
